import { useQuery } from "@tanstack/react-query"
import { ArrowRight, Briefcase, Building2, Inbox, Plus, Sparkles, Users } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { Link } from "react-router-dom"
import { jobsApi } from "@/api/jobs"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { PageHeader } from "@/components/dashboard/page-header"
import { StatCardsSkeleton } from "@/components/shared/loading-skeletons"
import { useAuth } from "@/hooks/use-auth"

export function DashboardHome() {
  const { user, activeOrg } = useAuth()
  const jobs = useQuery({ queryKey: ["jobs"], queryFn: () => jobsApi.list() })

  const openCount = jobs.data?.data.filter((j) => j.status === "open").length ?? 0
  const totalCount = jobs.data?.data.length ?? 0
  const draftCount = jobs.data?.data.filter((j) => j.status === "draft").length ?? 0

  return (
    <>
      <PageHeader
        variant="gradient"
        eyebrow={
          <span className="inline-flex items-center gap-1.5">
            <Sparkles className="size-3" /> AI destekli işe alım
          </span>
        }
        title={`Hoş geldin${user?.full_name ? `, ${user.full_name.split(" ")[0]}` : ""}`}
        description={
          activeOrg
            ? `${activeOrg.organization.name} · ${activeOrg.organization.plan} planı`
            : "İşe alım süreçlerini buradan yönet."
        }
        action={
          <Link to="/dashboard/jobs/new">
            <Button>
              <Plus className="size-4" /> Yeni ilan
            </Button>
          </Link>
        }
      />
      <div className="space-y-6 p-4 sm:p-6">
        {jobs.isLoading ? (
          <StatCardsSkeleton count={3} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              icon={Briefcase}
              label="Açık ilan"
              value={openCount}
              hint={draftCount ? `${draftCount} taslakta bekliyor` : undefined}
              to="/dashboard/jobs?status=open"
              tone="primary"
            />
            <StatCard
              icon={Users}
              label="Toplam ilan"
              value={totalCount}
              to="/dashboard/jobs"
            />
            <StatCard
              icon={Building2}
              label="Aktif organizasyon"
              value={activeOrg?.organization.name ?? "—"}
              hint={activeOrg ? `Plan: ${activeOrg.organization.plan}` : undefined}
              to="/dashboard/settings"
            />
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <QuickAction
            to="/dashboard/jobs/new"
            icon={Plus}
            title="Yeni ilan oluştur"
            description="AI destekli taslakla saniyeler içinde yayına gir."
          />
          <QuickAction
            to="/dashboard/pipeline"
            icon={Inbox}
            title="Pipeline'a gir"
            description="Açık ilanlardaki adayları sürükle-bırak ile yönet."
          />
        </div>
      </div>
    </>
  )
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  to,
  tone = "default",
}: {
  icon: LucideIcon
  label: string
  value: number | string
  hint?: string
  to: string
  tone?: "default" | "primary"
}) {
  return (
    <Link to={to} className="group">
      <Card className="h-full transition-all group-hover:border-primary/40 group-hover:shadow-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
          <span
            className={
              tone === "primary"
                ? "grid size-8 place-items-center rounded-md bg-primary-soft text-primary"
                : "grid size-8 place-items-center rounded-md bg-muted text-muted-foreground"
            }
          >
            <Icon className="size-4" />
          </span>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-semibold tracking-tight">{value}</div>
          {hint && (
            <Badge variant="secondary" className="mt-2 font-normal">
              {hint}
            </Badge>
          )}
        </CardContent>
      </Card>
    </Link>
  )
}

function QuickAction({
  to,
  icon: Icon,
  title,
  description,
}: {
  to: string
  icon: LucideIcon
  title: string
  description: string
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-4 rounded-xl border bg-card p-4 transition-all hover:border-primary/40 hover:shadow-sm"
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-primary-soft text-primary">
        <Icon className="size-5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-medium">{title}</div>
        <div className="truncate text-xs text-muted-foreground">{description}</div>
      </div>
      <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
    </Link>
  )
}
